import Alert from '@/common/components/atom/alert/alert';
import Text from '@/common/components/atom/text';
import WarningIcon from '@/common/components/icons/warning';
import { CENTERS } from '@/common/types/centers';
import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';
import { useState, useEffect } from 'react';
import Discount from '../../components/factor/discount';
import Invoice from '../../components/factor/invoice';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { FACTOR_BALANCE_VERSION, useGetBalance } from '@/common/apis/services/wallet/getBalance';
import { useGetPaymentMethods } from '@/common/apis/services/factor/paymentMethods';
import { useUserInfoStore } from '@/modules/login/store/userInfo';
import PaymentMethods from '../../components/factor/paymentMethods';
import { FACTOR_EXTRA_WALLET_TOPUP_FEATURE, isSnappPaymentMethod } from '@/modules/booking/constants/extraWalletTopup';
import { tomanToRial } from '@/modules/booking/utils/encodeExtraWalletTopupPaymentMethod';
import { usesKatibeFactorPayment } from '@/modules/booking/utils/usesKatibeFactorPayment';

interface FactorProps {
  bookId: string;
  centerId: string;
  price?: string;
  tax?: string;
  /*
    discount
  */
  discount?: string;
  discountToken?: string;
  discountErrorMessage?: string;
  discountLoading?: boolean;
  isShowDiscountInput?: boolean;
  isValidDiscount?: boolean;
  totalPrice?: string;
  rules?: string[];
  loading: boolean;
  onSubmitDiscount: (code: string) => void;
  onPayment: ({ discountToken, bookId, paymentMethod }: { discountToken?: string; bookId: string; paymentMethod?: string }) => void;
  selectedPaymentMethod?: string;
  /** byUser=false when the system picked the method (default / fallback). */
  onSelectionChange?: (paymentMethod: string, byUser?: boolean) => void;
  extraWalletTopupEnabled?: boolean;
  extraWalletTopupToman?: number;
  onExtraWalletTopupEnabledChange?: (enabled: boolean) => void;
  onExtraWalletTopupTomanChange?: (toman: number) => void;
  onPayableAmountChange?: (amount?: number) => void;
}
export const Factor = (props: FactorProps) => {
  const {
    centerId,
    price,
    totalPrice,
    tax,
    rules = [],
    onSubmitDiscount,
    discount,
    isValidDiscount,
    discountErrorMessage,
    isShowDiscountInput = false,
    discountLoading,
    loading,
    selectedPaymentMethod: propSelectedPaymentMethod,
    onSelectionChange,
    extraWalletTopupEnabled = false,
    extraWalletTopupToman = 0,
    onExtraWalletTopupEnabledChange,
    onExtraWalletTopupTomanChange,
    onPayableAmountChange,
  } = props;

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>(propSelectedPaymentMethod || '');

  const useKatibePaymentForEarnestFactor = useFeatureIsOn('use-katibe-payment-for-earnest-factor');
  const usesKatibePaymentUi = usesKatibeFactorPayment(centerId, useKatibePaymentForEarnestFactor);
  const isExtraWalletTopupFeatureOn = useFeatureIsOn(FACTOR_EXTRA_WALLET_TOPUP_FEATURE);
  const isLogin = useUserInfoStore(state => state.isLogin);
  const userInfo = useUserInfoStore(state => state.info);

  const timezone =
    typeof Intl?.DateTimeFormat?.()?.resolvedOptions()?.timeZone === 'string'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : undefined;

  // Non-Katibe centers (most hospitals) pay via center-payment's own gateway — skip Katibe APIs.
  const walletBalanceQueryEnabled = usesKatibePaymentUi && isLogin;

  const {
    data: balance,
    isLoading: balanceLoading,
    isSuccess: balanceSuccess,
  } = useGetBalance({
    enabled: walletBalanceQueryEnabled,
    version: FACTOR_BALANCE_VERSION,
  });

  const balanceForPaymentMethods =
    balanceSuccess && typeof balance?.data?.data?.balance === 'number' ? balance.data.data.balance : undefined;

  const { data: paymentMethodsData, isFetching: paymentMethodsFetching } = useGetPaymentMethods(
    {
      amount: totalPrice,
      timezone,
      countryCode: userInfo?.country_code_id,
      center_id: centerId,
      ...(balanceForPaymentMethods !== undefined ? { balance: balanceForPaymentMethods } : {}),
    },
    {
      enabled: usesKatibePaymentUi && !!totalPrice && !loading,
      keepPreviousData: true,
    },
  );
  const apiPaymentMethods = paymentMethodsData?.data?.data?.payment_methods || [];
  const guestPaymentMethods = [
    { payment_method: 'saman', title: 'درگاه پرداخت سامان', logo: 'https://apigw.paziresh24.com/katibe/files/payment-method/sep.svg', html_content: '', display_amount: '' },
  ];
  // Katibe path: API list (local saman only until it responds). Non-Katibe: no methods UI; center-payment uses its gateway.
  const paymentMethods = !usesKatibePaymentUi ? [] : !paymentMethodsData ? guestPaymentMethods : apiPaymentMethods;
  const additionalContent = usesKatibePaymentUi ? paymentMethodsData?.data?.data?.additional_html || '' : '';
  const payment_description_html = usesKatibePaymentUi ? paymentMethodsData?.data?.data?.payment_description_html || '' : '';

  const handlePaymentMethodSelection = (paymentMethod: string) => {
    setSelectedPaymentMethod(paymentMethod);
    onSelectionChange?.(paymentMethod, true);
  };

  const walletBalanceRial = typeof balance?.data?.data?.balance === 'number' ? balance.data.data.balance : 0;
  const totalPriceRial = totalPrice != null && totalPrice !== '' ? Number(totalPrice) : NaN;
  const isWalletMethodSelected = String(selectedPaymentMethod || '')
    .toLowerCase()
    .startsWith('wallet');
  const isPayingFullyFromWallet = isWalletMethodSelected && Number.isFinite(totalPriceRial) && walletBalanceRial >= totalPriceRial;
  const extraWalletTopupRial = extraWalletTopupEnabled && !isPayingFullyFromWallet ? tomanToRial(extraWalletTopupToman) : 0;
  const showExtraWalletTopup =
    usesKatibePaymentUi &&
    isExtraWalletTopupFeatureOn &&
    isLogin &&
    !loading &&
    Number.isFinite(totalPriceRial) &&
    totalPriceRial > 0 &&
    !isPayingFullyFromWallet;
  const snappDisabledByExtraTopup = extraWalletTopupRial > 0;
  const disabledPaymentMethods = snappDisabledByExtraTopup
    ? paymentMethods.filter(m => isSnappPaymentMethod(m.payment_method)).map(m => m.payment_method)
    : [];

  useEffect(() => {
    if (isPayingFullyFromWallet && extraWalletTopupEnabled) {
      onExtraWalletTopupEnabledChange?.(false);
    }
  }, [isPayingFullyFromWallet, extraWalletTopupEnabled, onExtraWalletTopupEnabledChange]);

  // Extra wallet top-up: keep Snapp visible but unselectable; if already selected, pick next available
  useEffect(() => {
    if (!snappDisabledByExtraTopup) return;
    if (!isSnappPaymentMethod(selectedPaymentMethod)) return;
    const next = paymentMethods.find(m => !isSnappPaymentMethod(m.payment_method));
    if (!next?.payment_method) return;
    setSelectedPaymentMethod(next.payment_method);
    onSelectionChange?.(next.payment_method, false);
  }, [snappDisabledByExtraTopup, selectedPaymentMethod, paymentMethods, onSelectionChange]);

  useEffect(() => {
    if (paymentMethods.length > 0 && (!selectedPaymentMethod || (!paymentMethodsFetching && !paymentMethods.some(method => method.payment_method === selectedPaymentMethod)))) {
      const firstSelectable =
        paymentMethods.find(m => !(snappDisabledByExtraTopup && isSnappPaymentMethod(m.payment_method))) || paymentMethods[0];
      const firstPaymentMethod = firstSelectable.payment_method;
      setSelectedPaymentMethod(firstPaymentMethod);
      onSelectionChange?.(firstPaymentMethod, false);
    }
  }, [paymentMethods, selectedPaymentMethod, onSelectionChange, snappDisabledByExtraTopup, paymentMethodsFetching]);

  return (
    <div className="flex flex-col space-y-2 md:space-y-5">
      {usesKatibePaymentUi && (
        <PaymentMethods
          paymentMethods={paymentMethods}
          additionalContent={additionalContent}
          selectedPaymentMethod={selectedPaymentMethod}
          onSelectionChange={handlePaymentMethodSelection}
          disabledPaymentMethods={disabledPaymentMethods}
        />
      )}
      {isShowDiscountInput && (
        <Discount
          loading={discountLoading}
          onSubmit={onSubmitDiscount}
          status={isValidDiscount !== undefined ? (isValidDiscount ? 'successful' : 'unSuccessful') : 'default'}
          errorMessage={discountErrorMessage}
        />
      )}
      <div className="flex flex-col justify-center px-5 py-5 space-y-3 bg-white md:rounded-lg shadow-card">
        <Invoice
          serviceFeeText={centerId === CENTERS.CONSULT ? 'کارمزد خدمات آنلاین' : 'مالیات و کارمزد خدمات آنلاین'}
          serviceFee={centerId === CENTERS.CONSULT ? 'پزشک پرداخت کرده' : 'رایگان'}
          priceText={centerId === CENTERS.CONSULT || !useKatibePaymentForEarnestFactor ? 'مبلغ ویزیت' : 'پیش پرداخت حق ویزیت (بیعانه)'}
          price={price}
          totalPrice={totalPrice}
          extraWalletTopupRial={extraWalletTopupRial}
          showExtraWalletTopup={showExtraWalletTopup}
          extraWalletTopupEnabled={extraWalletTopupEnabled}
          extraWalletTopupToman={extraWalletTopupToman}
          onExtraWalletTopupEnabledChange={onExtraWalletTopupEnabledChange}
          onExtraWalletTopupTomanChange={onExtraWalletTopupTomanChange}
          walletAmount={usesKatibePaymentUi ? balance?.data?.data?.balance : undefined}
          tax={tax}
          discount={discount}
          payment_description_html={payment_description_html || ''}
          loading={loading || (usesKatibePaymentUi && isLogin && balanceLoading)}
          onPayableAmountChange={onPayableAmountChange}
        />
      </div>
      {!isEmpty(rules) && !loading && (
        <Alert severity="warning" className="p-5 rounded-none md:rounded-lg">
          <div className="flex items-strat gap-2 mb-2">
            <WarningIcon className="-translate-y-1 text-[#975b2a]" />
            <Text fontWeight="bold">لطفا دقت کنید!</Text>
          </div>
          <ul className="mr-5 list-disc space-y-4">
            {rules.map((rule, index) => [
              <li key={index}>
                <Text fontSize="sm" className={clsx({ 'text-[#975b2a]': index === 0 })} dangerouslySetInnerHTML={{ __html: rule }} />
              </li>,
            ])}
          </ul>
        </Alert>
      )}
    </div>
  );
};
export default Factor;
